import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Step1 from './Step1';
import { compose } from '../../services/utilityFunctions';
import translate from '../../commonComponents/translate';
import withCSRF from '../../commonComponents/withCSRF';
import Notifications from '../../services/notifications';
import Step2 from './Step2';
import Step3 from '../Step3';

const SESSION_STORAGE_FLAG = 'alreadyRegistered';

function gtag(...args) {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(...args);
  }
}

function Trial({ t, _csrf, tenant, browserLanguage, countries }) {
  const [step, setStep] = useState(1);

  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organizationName: '',
    password: '',
    confirmPassword: '',
  });
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [alreadyInvited, setAlreadyInvited] = useState(false);
  const [portalActive, setPortalActive] = useState('user');

  // prevent user from coming back to form if didn't close browser
  useEffect(() => {
    setStep(sessionStorage.getItem(SESSION_STORAGE_FLAG) ? 2 : 1);
    setInitialLoad(false);
    const userLocale = navigator.language;
    const countryCode = userLocale.split('-')[1];
    const defaultCountry =
      countries.find(c => c.code === countryCode) || countries[0];
    setForm(prevForm => ({ ...prevForm, countryPhone: defaultCountry }));
  }, []);

  const handleFormChange = (field, value) => {
    setForm(prevForm => ({ ...prevForm, [field]: value }));
  };

  const handleSubmitStep1 = async () => {
    try {
      setIsLoading(true);

      const params = {
        ...form,
        tenant: tenant._id,
        language: browserLanguage,
        phone: `${form.countryPhone.phone} ${form.phone}`,
        country: form.countryPhone.name,
        shippingCountry: form.countryPhone.code,
      };
      delete params.confirmPassword;
      delete params.countryPhone;

      const { data: response } = await axios.post('/trial', params, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Organization',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      });

      await axios.post(
        '/signin',
        {
          email: form.email,
          password: form.password,
          organizationIdentifier: response.data.identifierCode,
          ignoreRedirect: true,
        },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Auth',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );

      sessionStorage.setItem('organization', response.data._id);
      sessionStorage.setItem(SESSION_STORAGE_FLAG, 'true');
      gtag({
        event: 'form_start_free_submited',
        formName: 'trial1Form',
        language: browserLanguage,
        countryCode: form.countryPhone.code,
      });
      setStep(2);
    } catch (err) {
      console.error(err);
      Notifications.showErrorFromRequest(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvitedUsersChange = newUser => {
    if (newUser.length > 9) {
      Notifications.showNotificationWarning(t('error'), t('error.inviteUsers'));
      return;
    }
    setInvitedUsers(Array.isArray(newUser) ? newUser : [newUser]);
  };

  const handleSubmitStep2 = async () => {
    try {
      setIsLoading(true);

      if (invitedUsers?.length > 0) {
        await axios.post(
          '/jobs/user/list?referenceWithEmails=false&sendInvitationEmails=true',
          {
            users: invitedUsers.map(email => ({
              email,
              type: 'collaborator',
              role: 'user',
              language: browserLanguage,
            })),
            tenant: tenant._id,
            organization: sessionStorage.getItem('organization'),
          },
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        );
        Notifications.showNotificationSuccess(
          t('success'),
          t('trial.users.invited'),
        );
        setAlreadyInvited(true);
      }

      setStep(3);
    } catch (err) {
      console.error(err);
      Notifications.showErrorFromRequest(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    setStep(2);
  };

  const handleSubmitStep3 = async () => {
    setIsLoading(true);
    const { data: response } = await axios.get(
      portalActive === 'user' ? '/user-portal' : 'admin-portal',
      {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Organization',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      },
    );
    window.location.href = response.data;
  };

  return (
    <div className="trial-container">
      {tenant?.branding?.horizontalLogo && (
        <div className="trial-image-wrapper">
          <img
            className="trial-image"
            src={tenant?.branding?.horizontalLogo}
            alt="Minds"
          />
        </div>
      )}
      <div
        className="trial-card"
        style={initialLoad ? { display: 'flex', alignItems: 'center' } : {}}
      >
        {initialLoad && (
          <div className="initial-spinner-container">
            <i className="fa fa-spinner initial-spinner" />
          </div>
        )}
        {!initialLoad && step === 1 && (
          <Step1
            form={form}
            onFormChange={handleFormChange}
            onSubmitForm={handleSubmitStep1}
            isLoading={isLoading}
            step={step}
            countries={countries}
          />
        )}

        {!initialLoad && step === 2 && (
          <Step2
            step={step}
            onInvitedUsersChange={handleInvitedUsersChange}
            invitedUsers={invitedUsers}
            onSubmit={handleSubmitStep2}
            isLoading={isLoading}
            tenant={tenant}
          />
        )}

        {!initialLoad && step === 3 && (
          <Step3
            step={step}
            onSubmit={handleSubmitStep3}
            onGoBack={handleGoBack}
            isLoading={isLoading}
            onClickCard={card => setPortalActive(card)}
            activeCard={portalActive}
            alreadyInvited={alreadyInvited}
            primaryColor={tenant?.branding?.primaryColor}
          />
        )}
      </div>
    </div>
  );
}

Trial.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  tenant: PropTypes.shape({
    _id: PropTypes.string,
    configs: PropTypes.shape({
      userPortal: PropTypes.string,
    }),
    branding: PropTypes.shape({
      appName: PropTypes.string,
      horizontalLogo: PropTypes.string,
    }),
    terms: PropTypes.shape({
      termsLink: PropTypes.string,
      privacyLink: PropTypes.string,
    }),
  }).isRequired,
  browserLanguage: PropTypes.string.isRequired,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      phone: PropTypes.string.isRequired,
      flagEmoji: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default compose(withCSRF, translate)(Trial);
